<template>
  <teleport to="body">
    <ag-loading v-if="loading" class="global-loading" />
  </teleport>

  <EditPanel
    v-model="visible"
    title="Import App Group"
    :form-component="formComponent"
    :form="formData"
    form-type="appGroupImport"
    :show-enabled="false"
    @save="onSave"       
    @cancel="onCancel"
  />

  <input
    type="file"
    ref="fileInput"
    accept=".json"
    style="display: none"
    @change="handleFileUpload"
  />
</template>

<script setup>
import { defineEmits,defineExpose, ref, reactive} from "vue";
import Swal from "sweetalert2";
import { useStore } from "@/store";
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import EditPanel from "@/components/ag-edit-panel/index.vue";
import ImportForm from "@/components/ag-edit-panel/forms/app-group-import-form.vue";
import AgLoading from "@/components/ag-loading/index.vue";
import { useRouter } from "vue-router";

const store = useStore();
const visible = ref(false);
const fileInput = ref(null);
const loading = ref(false);
const router = useRouter();
const formData = reactive({
  name: "",
  fileName: "",
  importedData: null
});

const formComponent = ImportForm;

const open = () => {
  if (fileInput.value) fileInput.value.value = "";
  fileInput.value?.click();
};

const handleFileUpload = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  formData.fileName = file.name;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const parsed = JSON.parse(e.target.result);
      formData.importedData = parsed;
      formData.name = parsed?.Name || parsed?.name || "";
      visible.value = true;
    } catch {
      Swal.fire("Error", "Invalid JSON file!", "error");
    }
  };
  reader.readAsText(file);
};

const onCancel = () => {
  visible.value = false;
  formData.name = "";
  formData.fileName = "";
  formData.importedData = null;
  if (fileInput.value) fileInput.value.value = "";
};


const onSave = async (updatedForm) => {
  try {
    if (!updatedForm?.importedData) {
      return Swal.fire("Error", "Please select a valid JSON file first.", "error");
    }

    const group = localStorage.getItem("group");
    const groupId = group ? JSON.parse(group)?.mainid : null;

    const payload = {
      ...updatedForm.importedData,
      Name: updatedForm.name,  
      GroupId: groupId,
      Enabled: false
    };

    loading.value = true;

    const response = await store.dispatch(
      GeneralActionTypes.IMPORT_APPGROUP,
      payload
    );

    if (response?.isSuccess) {
      Swal.fire("Success", "AppGroup imported successfully!", "success");
      
      await store.dispatch(GeneralActionTypes.GET_APPGROUPS);
      router.push("/administration/app-group-management");
      onCancel();
      
    } else {
      Swal.fire("Error", response?.message || "Import failed.", "error");
    }
  } catch (err) {
    console.error(err);
    const serverData = err?.response?.data ?? err?.data ?? null;
    let msg = "";

    if (serverData?.errors) {
      msg = Array.isArray(serverData.errors) ? serverData.errors.join("<br>") : serverData.errors;
    } else if (serverData?.message) {
      msg = serverData.message;
    } else if (err?.message) {
      msg = err.message;
    } else {
      msg = JSON.stringify(err);
    }

    Swal.fire("Error", msg || "Failed to import AppGroup.", "error");
  } finally {
    loading.value = false;
  }
};

defineExpose({ open });
</script>

<style scoped>
.global-loading {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(255, 255, 255, 0.349) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 99999 !important;
}
</style>
